import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Stack,
  Typography,
  Button,
} from '@mui/material';

import { translate as t } from 'hooks/useTranslations';

import Modal from 'components/_layouts/Modal';
import { AppContext } from 'views/App/context';
import useReviewWorkflows from 'views/App/Portal/PerformanceReporting/ReviewWorkflows/_data_sources/useReviewWorkflows';

interface PublishTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { changeNote: string; reviewWorkflowId: number }) => Promise<void> | void;
  isSaving: boolean;
}

const PublishTemplateDialog: React.FC<PublishTemplateDialogProps> = ({ open, onClose, onConfirm, isSaving }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [selectedReviewWorkflowId, setSelectedReviewWorkflowId] = useState<number | ''>('');
  const [changeNote, setChangeNote] = useState('');
  const [publishTouched, setPublishTouched] = useState(false);

  const queryParams = useMemo(() => {
    return {
      organizationId: currentOrganizationId,
    };
  }, [currentOrganizationId]);
  const reviewWorkflowsQuery = useReviewWorkflows(queryParams);
  const reviewWorkflows = reviewWorkflowsQuery.data?.pages.flatMap((page) => page.content) ?? [];
  const { isLoading: isReviewWorkflowsLoading } = reviewWorkflowsQuery;

  const resetState = useCallback(() => {
    setSelectedReviewWorkflowId('');
    setChangeNote('');
    setPublishTouched(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const reviewWorkflowError = publishTouched && !selectedReviewWorkflowId;
  const canPublish = Boolean(selectedReviewWorkflowId);

  const reviewWorkflowHelperText = useMemo(() => {
    if (reviewWorkflowError) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowRequired');
    }

    if (isReviewWorkflowsLoading) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowLoading');
    }

    if (!reviewWorkflows.length) {
      return t('performanceReporting.templates.builder.publishModal.reviewWorkflowEmpty');
    }

    return t('performanceReporting.templates.builder.publishModal.reviewWorkflowHelper');
  }, [isReviewWorkflowsLoading, reviewWorkflowError, reviewWorkflows.length]);

  const changeNoteHelperText = t('performanceReporting.templates.builder.publishModal.changeNoteHelper');

  const handleConfirm = useCallback(async () => {
    const trimmedChangeNote = changeNote.trim();
    setPublishTouched(true);

    if (!selectedReviewWorkflowId) {
      return;
    }

    await onConfirm({
      changeNote: trimmedChangeNote,
      reviewWorkflowId: Number(selectedReviewWorkflowId),
    });
  }, [changeNote, onConfirm, selectedReviewWorkflowId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('performanceReporting.templates.builder.publishModal.title')}
      actions={
        <>
          <Button
            variant='outlined'
            color='primary'
            onClick={onClose}
            disabled={isSaving}
          >
            {t('performanceReporting.templates.builder.publishModal.keepEditing')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirm}
            disabled={!canPublish || isSaving || isReviewWorkflowsLoading || !reviewWorkflows.length}
            loading={isSaving}
            loadingPosition='end'
          >
            {t('performanceReporting.templates.builder.publishModal.publish')}
          </Button>
        </>
      }
    >
      <Stack
        spacing={3}
        sx={{ mt: 1 }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t('performanceReporting.templates.builder.publishModal.description')}
        </Typography>
        <FormControl
          fullWidth
          required
          error={reviewWorkflowError}
          disabled={isReviewWorkflowsLoading || !reviewWorkflows.length}
        >
          <InputLabel id='publish-review-workflow-label'>
            {t('performanceReporting.templates.builder.publishModal.reviewWorkflowLabel')}
          </InputLabel>
          <Select
            labelId='publish-review-workflow-label'
            value={selectedReviewWorkflowId}
            label={t('performanceReporting.templates.builder.publishModal.reviewWorkflowLabel')}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedReviewWorkflowId(value === '' ? '' : Number(value));
            }}
            displayEmpty
          >
            <MenuItem
              value=''
              disabled
            >
              {t('performanceReporting.templates.builder.publishModal.reviewWorkflowPlaceholder')}
            </MenuItem>
            {reviewWorkflows.map((workflow) => (
              <MenuItem
                key={workflow.reviewWorkflowId}
                value={workflow.reviewWorkflowId}
              >
                {workflow.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{reviewWorkflowHelperText}</FormHelperText>
        </FormControl>
        <TextField
          label={t('performanceReporting.templates.builder.publishModal.changeNoteLabel')}
          value={changeNote}
          onChange={(event) => setChangeNote(event.target.value)}
          fullWidth
          multiline
          minRows={3}
          helperText={changeNoteHelperText}
        />
      </Stack>
    </Modal>
  );
};

export default PublishTemplateDialog;
